import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

function formatApiError(detail) {
  if (detail == null) return "Accesso non riuscito. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
}

export default function AdminLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Accesso non riuscito.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="noise-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm"
      >
        <div className="bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-2xl p-8 md:p-10">
          <div className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center mb-8">
            <Lock className="w-5 h-5 text-[#007AFF]" />
          </div>
          <h1 className="font-display font-bold tracking-tight text-2xl" data-testid="admin-login-title">Area Admin</h1>
          <p className="mt-2 text-sm text-zinc-500">Accedi per modificare i contenuti del sito.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="admin-email" className="text-zinc-400">Email</Label>
              <Input
                id="admin-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email-input"
                className="bg-black/40 border-white/15 focus-visible:ring-[#007AFF]"
                placeholder="admin@studioai.it"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password" className="text-zinc-400">Password</Label>
              <Input
                id="admin-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password-input"
                className="bg-black/40 border-white/15 focus-visible:ring-[#007AFF]"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-button"
              className="w-full rounded-full bg-white text-black hover:bg-[#007AFF] hover:text-white py-6 text-sm font-semibold transition-colors"
            >
              {loading ? "Accesso..." : "Accedi"}
            </Button>
          </form>
        </div>
      </motion.div>
    </main>
  );
}
